/* Product Aliases — Documents sub-panel (Iter 150, price-list upgrade Iter 229). */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LearnedChipX, RecentlyLearnedButton, useLearnedLog } from "@/components/LearnedGenerics";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackingInline, PackingProgress, PackingSheetButtons, perPill } from "@/components/PartnerPackingMissing";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Search, Trash2, Pencil, RefreshCw, Download, Package, X, Upload, FileSpreadsheet,
  ArrowUp, ArrowDown, ArrowUpDown, GitMerge,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ALIASES = 20;
const COLS = 10;
const authHeader = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const emptyDraft = () => ({
  id: null, second_name: "", packing: "", s_no: "", generic_name: "",
  company: "", price: "", branded_mrp: "", aliases: [""],
});

// Iter 231 — highlight matched search tokens inside cells
const _escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const _tokPat = (t) => {
  const parts = t.match(/\d+|[a-zA-Z]+/g);
  return parts ? parts.map(_escRe).join("[\\s.\\-_]*") : _escRe(t);
};
const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  const toks = (q || "").trim().split(/\s+/).filter(Boolean).slice(0, 6);
  if (!s || !toks.length) return s;
  let rx;
  try { rx = new RegExp(`(${toks.map(_tokPat).join("|")})`, "ig"); }
  catch { return s; }
  return s.split(rx).map((p, i) => i % 2 === 1
    ? <mark key={i} className="bg-amber-200 text-inherit rounded-[2px] px-px">{p}</mark>
    : p);
};

export default function ProductAliasesPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [pkTick, setPkTick] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [renderLimit, setRenderLimit] = useState(200);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Iter 241 — server-side sorting across the whole collection
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  // Iter 244 — duplicates finder + merge
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupKeep, setDupKeep] = useState({}); // {groupKey: keeperId}
  const [merging, setMerging] = useState(null);

  const openDuplicates = async () => {
    setDupOpen(true);
    setDupLoading(true);
    try {
      const r = await axios.get(`${API}/product-aliases/duplicates`,
        { headers: authHeader() });
      setDupGroups(r.data.groups || []);
      const keepInit = {};
      (r.data.groups || []).forEach(g => { keepInit[g.key] = g.docs[0]?.id; });
      setDupKeep(keepInit);
    } catch (e) {
      toast.error("Failed to load duplicates");
    } finally { setDupLoading(false); }
  };

  const mergeGroup = async (g) => {
    const keep_id = dupKeep[g.key];
    if (!keep_id) return;
    const keeper = g.docs.find(d => d.id === keep_id) || g.docs[0];
    const merge_ids = g.docs.filter(d => d.id !== keep_id).map(d => d.id);
    setMerging(g.key);
    try {
      const r = await axios.post(`${API}/product-aliases/merge`,
        { keep_id, merge_ids, second_name: keeper.second_name },
        { headers: authHeader() });
      toast.success(`Merged ${r.data.merged} into "${r.data.second_name}" (${r.data.aliases_count} aliases)`);
      setDupGroups(prev => prev.filter(x => x.key !== g.key));
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setMerging(null); }
  };

  const learned = useLearnedLog();
  const afterUndo = () => { learned.reload(); fetchRows(); };
  const fetchRows = useCallback(async (skip = 0, append = false) => {
    setLoading(true);
    try {
      // Iter 240 — ask the server for a capped page; the panel renders
      // progressively (Show more) so 7k+ rows never freeze the browser.
      // Iter 242 — `skip`/`append` page through the server results so a
      // sorted/searched view can browse beyond the first 500.
      const res = await axios.get(`${API}/product-aliases`, {
        headers: authHeader(),
        params: {
          ...(q ? { q } : {}), limit: 500, skip,
          ...(sortKey ? { sort: sortKey, order: sortDir === 1 ? "asc" : "desc" } : {}),
        },
      });
      const fresh = res.data.rows || [];
      setRows(prev => append ? [...prev, ...fresh] : fresh);
      setTotalCount(res.data.count ?? fresh.length);
      if (append) setRenderLimit(l => Math.max(l, skip + 200));
      else setRenderLimit(200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, [q, sortKey, sortDir]);
  useEffect(() => {
    const t = setTimeout(fetchRows, 300);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const openCreate = () => { setDraft(emptyDraft()); setEditorOpen(true); };
  const openEdit = (row) => {
    setDraft({
      id: row.id, second_name: row.second_name, packing: row.packing || "",
      s_no: row.s_no || "", generic_name: row.generic_name || "",
      company: row.company || "", price: row.price || "",
      branded_mrp: row.branded_mrp || "",
      aliases: (row.aliases && row.aliases.length ? row.aliases : [""]),
    });
    setEditorOpen(true);
  };
  const updateAlias = (i, v) => setDraft(d => ({ ...d, aliases: d.aliases.map((a, j) => j === i ? v : a) }));
  const addAlias = () => setDraft(d => d.aliases.length >= MAX_ALIASES
    ? d : ({ ...d, aliases: [...d.aliases, ""] }));
  const removeAlias = (i) => setDraft(d => ({ ...d, aliases: d.aliases.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!draft.second_name.trim()) { toast.error("Enter a canonical Second Name"); return; }
    const filled = draft.aliases.map(a => a.trim()).filter(Boolean);
    setSaving(true);
    try {
      await axios.post(`${API}/product-aliases`,
        { second_name: draft.second_name.trim(), aliases: filled,
          packing: draft.packing.trim(), s_no: draft.s_no.trim(),
          generic_name: draft.generic_name.trim(), company: draft.company.trim(),
          price: draft.price.trim(), branded_mrp: draft.branded_mrp.trim() },
        { headers: authHeader() });
      toast.success("Saved");
      setEditorOpen(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/product-aliases/${confirmDelete.id}`, { headers: authHeader() });
      toast.success("Deleted");
      setConfirmDelete(null);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/product-aliases/export`,
        { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ProductAliases_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) {
      const st = e.response?.status;
      toast.error(st === 403 ? "Export is admin-only" : "Export failed — please try again");
    }
  };

  // Iter 233 — uploads go through a mode-confirmation dialog first:
  // "upgrade" only fills blank fields + appends aliases (never disturbs
  // existing data); "replace" overwrites the sheet's columns.
  // Iter 235 — "Preview changes" fetches a diff before applying.
  const [pendingUpload, setPendingUpload] = useState(null); // {kind, file}
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null); // {mode, counters, changes}
  const [previewLoading, setPreviewLoading] = useState(false);

  const pickFile = (kind) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingUpload({ kind, file });
    setPreviewData(null);
  };

  const runPreview = async (mode) => {
    if (!pendingUpload) return;
    const { kind, file } = pendingUpload;
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = kind === "pricelist"
        ? `${API}/product-aliases/upload-pricelist?mode=${mode}&preview=true`
        : `${API}/product-aliases/upload?mode=${mode}&preview=true`;
      const res = await axios.post(url, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setPreviewData({ mode, ...res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally { setPreviewLoading(false); }
  };

  const doUpload = async (mode) => {
    if (!pendingUpload) return;
    const { kind, file } = pendingUpload;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = kind === "pricelist"
        ? `${API}/product-aliases/upload-pricelist?mode=${mode}`
        : `${API}/product-aliases/upload?mode=${mode}`;
      const res = await axios.post(url, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      const modeLabel = mode === "replace" ? "Replace" : "Upgrade";
      toast.success(kind === "pricelist"
        ? `Price list (${modeLabel}) — ${d.rows} rows: ${d.created} created, ${d.updated} updated, ` +
          `${d.aliases_added} alias${d.aliases_added === 1 ? "" : "es"} added` +
          (d.capped ? ` · ${d.capped} hit the ${MAX_ALIASES}-alias cap` : "")
        : `Upload (${modeLabel}) — ${d.created} created, ${d.updated} updated, ` +
          `${d.aliases_added} alias${d.aliases_added === 1 ? "" : "es"} added, ${d.packing_set} packing set` +
          (d.skipped ? ` · ${d.skipped} skipped` : "") +
          (d.capped ? ` · ${d.capped} hit the ${MAX_ALIASES}-alias cap` : ""));
      setPendingUpload(null);
      setPreviewData(null);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  // Iter 232/241 — header sorting; server-side since Iter 241 so it
  // covers every row in the collection, not just the loaded page.
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => -d);
    else { setSortKey(k); setSortDir(1); }
  };
  const Th = ({ k, label, className = "" }) => (
    <th className={`p-2 border border-slate-200 ${className}`}
        onClick={() => toggleSort(k)} data-testid={`pa-sort-${k}`}>
      <span className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-900">
        {label}
        {sortKey === k
          ? (sortDir === 1 ? <ArrowUp className="w-3 h-3 text-indigo-600" />
                           : <ArrowDown className="w-3 h-3 text-indigo-600" />)
          : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </span>
    </th>
  );

  const cell = (v, testid) => (
    <td className="p-2 border border-slate-200 text-xs text-slate-700" data-testid={testid}>
      {v ? <Hi text={v} q={q} /> : <span className="text-slate-300">—</span>}
    </td>
  );

  return (
    <div className="space-y-4" data-testid="product-aliases-panel">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Product Aliases
            </CardTitle>
            <div className="text-xs text-slate-500">
              Map up to {MAX_ALIASES} alias medicine names to one canonical Product Name.
              When ops types an alias in Order Register → Medicine, the Second Name auto-fills.
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search anything — e.g. azithromycin 250 (strength-wise)"
                     className="pl-8 h-9" data-testid="pa-search" />
            </div>
            {isAdmin && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={openCreate} data-testid="pa-new">
                <Plus className="w-4 h-4 mr-1" /> New Alias Mapping
              </Button>
            )}
            {isAdmin && (
              <>
                <input type="file" accept=".xlsx,.xlsm" className="hidden"
                       id="pa-upload-input" data-testid="pa-upload-input"
                       onChange={pickFile("alias")} />
                <Button size="sm" variant="outline"
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => document.getElementById("pa-upload-input").click()}
                        disabled={uploading}
                        title="Excel: PRODUCT (alias) | 2ND NAME | [QUANTITY PIL/SACH/VIAL — skipped] | QTY (packing). Missing products are auto-created."
                        data-testid="pa-upload">
                  {uploading && pendingUpload?.kind === "alias"
                    ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    : <Upload className="w-4 h-4 mr-1" />}
                  Upload Excel
                </Button>
                <input type="file" accept=".xlsx,.xlsm" className="hidden"
                       id="pa-pricelist-input" data-testid="pa-pricelist-input"
                       onChange={pickFile("pricelist")} />
                <Button size="sm" variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => document.getElementById("pa-pricelist-input").click()}
                        disabled={uploading}
                        title="EAC price list: S.No | Product Name | Generic Name | Company | Price | Branded MRP | 2nd–sixth Name (aliases). Product Name is the canonical name; aliases are appended."
                        data-testid="pa-upload-pricelist">
                  {uploading && pendingUpload?.kind === "pricelist"
                    ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                  Upload Price List
                </Button>
              </>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="pa-export">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            )}
            {isAdmin && <PackingSheetButtons scope="all" onDone={() => { fetchRows(0, false); setPkTick((t) => t + 1); }} />}
            {isAdmin && <PackingProgress tick={pkTick} />}
            {isAdmin && (
              <Button size="sm" variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={openDuplicates}
                      title="Find products with the same spelling (case / spaces / punctuation ignored) and merge them."
                      data-testid="pa-duplicates">
                <GitMerge className="w-4 h-4 mr-1" /> Find Duplicates
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      onClick={async () => {
                        try {
                          toast.info("Backfilling normalised keys…");
                          const r = await axios.post(`${API}/product-aliases/backfill-norm`,
                            {}, { headers: authHeader() });
                          toast.success(`Backfilled ${r.data.updated} of ${r.data.scanned} rows`);
                          fetchRows();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Backfill failed");
                        }
                      }}
                      title="One-time: recompute the normalised key on every legacy row so no new duplicates can slip in."
                      data-testid="pa-backfill">
                <RefreshCw className="w-4 h-4 mr-1" /> Backfill Keys
              </Button>
            )}
            <RecentlyLearnedButton rows={learned.rows} onChanged={afterUndo} />
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                    onClick={fetchRows} data-testid="pa-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <div className="ml-auto text-xs text-slate-500" data-testid="pa-count">
              {totalCount} mapping{totalCount === 1 ? "" : "s"}
              {totalCount > rows.length && ` · first ${rows.length} loaded`}
            </div>
          </div>

          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <Th k="s_no" label="S.No" className="text-center w-14" />
                  <Th k="second_name" label="Product Name (canonical)" className="text-left w-56" />
                  <Th k="generic_name" label="Generic Name" className="text-left w-36" />
                  <Th k="company" label="Company" className="text-left w-28" />
                  <Th k="price" label="Price" className="text-right w-20" />
                  <Th k="branded_mrp" label="Branded MRP" className="text-right w-24" />
                  <Th k="packing" label="Pills / strip" className="text-center w-20" />
                  <th className="p-2 text-left border border-slate-200">Aliases (dbl-click a row to edit)</th>
                  <th className="p-2 text-right border border-slate-200 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={COLS} className="text-center text-slate-500 py-6">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={COLS} className="text-center text-slate-500 py-6" data-testid="pa-empty">
                    No alias mappings yet. Click <b>New Alias Mapping</b> or <b>Upload Price List</b> to add some.
                  </td></tr>
                ) : (
                  rows.slice(0, renderLimit).map(r => (
                    <tr key={r.id} data-testid={`pa-row-${r.id}`}
                        onDoubleClick={() => isAdmin && openEdit(r)}
                        className={`border-t hover:bg-slate-50 ${isAdmin ? 'cursor-pointer' : ''}`}
                        title={isAdmin ? "Double-click to edit" : ""}>
                      <td className="p-2 text-center border border-slate-200 text-xs text-slate-500"
                          data-testid={`pa-sno-${r.id}`}>
                        {r.s_no ? <Hi text={r.s_no} q={q} /> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-2 border border-slate-200 font-semibold text-slate-800"><Hi text={r.second_name} q={q} /></td>
                      {cell(r.generic_name, `pa-generic-${r.id}`)}
                      {cell(r.company, `pa-company-${r.id}`)}
                      <td className="p-2 text-right border border-slate-200 text-xs text-slate-700"
                          data-testid={`pa-price-${r.id}`}>
                        {r.price || <span className="text-slate-300">—</span>}
                        {perPill(r.price, r.packing) && <div className="text-[10px] text-slate-400" data-testid={`pa-pill-${r.id}`}>{perPill(r.price, r.packing)}</div>}
                      </td>
                      <td className="p-2 text-right border border-slate-200 text-xs text-slate-700"
                          data-testid={`pa-mrp-${r.id}`}>
                        {r.branded_mrp || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-2 text-center border border-slate-200" data-testid={`pa-packing-${r.id}`}>
                        {isAdmin ? <PackingInline r={r} onSaved={(pk) => { setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, packing: pk } : x))); setPkTick((t) => t + 1); }} />
                          : r.packing
                          ? <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold">{r.packing}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-2 border border-slate-200">
                        <div className="flex flex-wrap gap-1">
                          {(r.aliases || []).map((a, i) => (
                            <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-mono cursor-pointer hover:bg-indigo-200 transition-colors"
                                  title="Click to search this alias"
                                  data-testid={`pa-alias-chip-${r.id}-${i}`}
                                  onClick={(e) => { e.stopPropagation(); setQ(a); }}>
                              <Hi text={a} q={q} />
                              {learned.byChip[`${r.id}|${a}`] && <LearnedChipX log={learned.byChip[`${r.id}|${a}`]} onDone={afterUndo} />}
                            </span>
                          ))}
                          {(r.aliases || []).length === 0 && (
                            <span className="text-slate-400 italic text-xs">no aliases yet</span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-right border border-slate-200 whitespace-nowrap">
                        {isAdmin && (
                          <>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                                    onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                                    data-testid={`pa-edit-${r.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600"
                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }}
                                    data-testid={`pa-delete-${r.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Iter 240/242 — progressive rendering + server paging */}
          {(rows.length > renderLimit || totalCount > rows.length) && (
            <div className="text-center space-y-1">
              {rows.length > renderLimit && (
                <Button size="sm" variant="outline"
                        onClick={() => setRenderLimit(l => l + 500)}
                        data-testid="pa-show-more">
                  Show more ({rows.length - renderLimit} remaining on this page)
                </Button>
              )}
              {rows.length <= renderLimit && totalCount > rows.length && (
                <Button size="sm" variant="outline"
                        onClick={() => fetchRows(rows.length, true)}
                        disabled={loading}
                        data-testid="pa-load-next">
                  {loading
                    ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    : null}
                  Load next 500 from server ({totalCount - rows.length} more)
                </Button>
              )}
              <p className="text-[10px] text-slate-400">
                Sorting covers all {totalCount} mappings across every page.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg" data-testid="pa-editor">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit" : "New"} Alias Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_96px] gap-2">
              <div>
                <Label className="text-xs">Product Name (canonical brand label) *</Label>
                <Input value={draft.second_name}
                       onChange={(e) => setDraft(d => ({ ...d, second_name: e.target.value }))}
                       placeholder="Vilitra 80 MG"
                       className="font-semibold"
                       data-testid="pa-editor-second-name" />
              </div>
              <div>
                <Label className="text-xs">S.No</Label>
                <Input value={draft.s_no}
                       onChange={(e) => setDraft(d => ({ ...d, s_no: e.target.value }))}
                       placeholder="1"
                       data-testid="pa-editor-sno" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Generic Name</Label>
                <Input value={draft.generic_name}
                       onChange={(e) => setDraft(d => ({ ...d, generic_name: e.target.value }))}
                       placeholder="SILDENAFIL"
                       data-testid="pa-editor-generic" />
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={draft.company}
                       onChange={(e) => setDraft(d => ({ ...d, company: e.target.value }))}
                       placeholder="SIGNAT"
                       data-testid="pa-editor-company" />
              </div>
              <div>
                <Label className="text-xs">Price</Label>
                <Input value={draft.price}
                       onChange={(e) => setDraft(d => ({ ...d, price: e.target.value }))}
                       placeholder="24.6"
                       data-testid="pa-editor-price" />
              </div>
              <div>
                <Label className="text-xs">Branded MRP</Label>
                <Input value={draft.branded_mrp}
                       onChange={(e) => setDraft(d => ({ ...d, branded_mrp: e.target.value }))}
                       placeholder="120"
                       data-testid="pa-editor-mrp" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Packing (units per strip/pack — e.g. 10)</Label>
              <Input value={draft.packing}
                     onChange={(e) => setDraft(d => ({ ...d, packing: e.target.value }))}
                     placeholder="10"
                     data-testid="pa-editor-packing" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Aliases (up to {MAX_ALIASES}) — matched case + punctuation insensitively</Label>
                {draft.aliases.length < MAX_ALIASES && (
                  <button className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium"
                          onClick={addAlias} data-testid="pa-editor-add-alias">
                    + Add alias
                  </button>
                )}
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {draft.aliases.map((a, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <Input value={a}
                           onChange={(e) => updateAlias(i, e.target.value)}
                           placeholder={`Alias ${i + 1} (e.g. vilitra-80mg)`}
                           className="h-8 text-xs"
                           data-testid={`pa-editor-alias-${i}`} />
                    {draft.aliases.length > 1 && (
                      <button className="text-rose-500 hover:text-rose-700 p-1"
                              onClick={() => removeAlias(i)}
                              title="Remove this alias"
                              data-testid={`pa-editor-remove-alias-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="pa-editor-save">
              {saving ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload mode confirmation (Iter 233) + Preview diff (Iter 235) */}
      <Dialog open={!!pendingUpload}
              onOpenChange={(o) => !o && !uploading && !previewLoading
                                    && (setPendingUpload(null), setPreviewData(null))}>
        <DialogContent className="max-w-2xl" data-testid="pa-upload-mode-dialog">
          <DialogHeader><DialogTitle>How should this sheet be applied?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p className="font-mono text-xs bg-slate-50 border rounded px-2 py-1 break-all" data-testid="pa-upload-filename">
              {pendingUpload?.file?.name}
            </p>
            <p><b className="text-emerald-700">Upgrade Only (safe)</b> — keeps every existing value.
              Only blank fields get filled and new aliases get appended.</p>
            <p><b className="text-rose-700">Replace Existing</b> — overwrites S.No / Generic /
              Company / Price / Branded MRP / Packing with this sheet's values. Existing alias
              names are still kept.</p>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => runPreview("upgrade")}
                      disabled={previewLoading || uploading}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="pa-preview-upgrade">
                {previewLoading && previewData?.mode !== "replace"
                  ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  : null}
                Preview Upgrade
              </Button>
              <Button size="sm" variant="outline" onClick={() => runPreview("replace")}
                      disabled={previewLoading || uploading}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid="pa-preview-replace">
                {previewLoading && previewData?.mode !== "upgrade"
                  ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  : null}
                Preview Replace
              </Button>
            </div>
            {previewData && (
              <div className="mt-2 border rounded-md" data-testid="pa-preview-panel">
                <div className={`px-3 py-1.5 text-xs font-semibold flex items-center justify-between
                                ${previewData.mode === "replace" ? "bg-rose-50 text-rose-800" : "bg-emerald-50 text-emerald-800"}`}>
                  <span data-testid="pa-preview-counters">
                    Preview ({previewData.mode === "replace" ? "Replace" : "Upgrade"}):
                    {" "}{previewData.created} created · {previewData.updated} updated ·
                    {" "}{previewData.aliases_added} alias{previewData.aliases_added === 1 ? "" : "es"} added
                    {previewData.fields_set !== undefined ? ` · ${previewData.fields_set} fields set` : ""}
                    {previewData.packing_set !== undefined ? ` · ${previewData.packing_set} packing set` : ""}
                    {previewData.capped ? ` · ${previewData.capped} capped` : ""}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {previewData.changes.length ? `Showing ${previewData.changes.length}` : "No changes"}
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto text-xs">
                  {previewData.changes.length === 0 ? (
                    <div className="p-3 text-slate-500 italic">No fields would change — this sheet matches current data.</div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr className="text-[10px] uppercase text-slate-500">
                          <th className="p-1.5 text-left">Product</th>
                          <th className="p-1.5 text-left">Kind</th>
                          <th className="p-1.5 text-left">Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.changes.map((c, i) => (
                          <tr key={i} className="border-t align-top" data-testid={`pa-preview-row-${i}`}>
                            <td className="p-1.5 font-semibold">{c.second_name}</td>
                            <td className="p-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                c.kind === "create"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-amber-100 text-amber-800"}`}>
                                {c.kind}
                              </span>
                            </td>
                            <td className="p-1.5 space-y-0.5">
                              {c.fields?.map((f, j) => (
                                <div key={j}>
                                  <span className="text-slate-500">{f.field}:</span>{" "}
                                  <span className="line-through text-rose-500">{f.before || "—"}</span>
                                  {" → "}
                                  <span className="text-emerald-700 font-semibold">{f.after}</span>
                                </div>
                              ))}
                              {c.packing && (
                                <div>
                                  <span className="text-slate-500">Packing:</span>{" "}
                                  <span className="line-through text-rose-500">{c.packing.before || "—"}</span>
                                  {" → "}
                                  <span className="text-emerald-700 font-semibold">{c.packing.after}</span>
                                </div>
                              )}
                              {(c.new_aliases?.length ? c.new_aliases : (c.new_alias ? [c.new_alias] : [])).map((a, j) => (
                                <div key={j}>
                                  <span className="text-slate-500">+ alias:</span>{" "}
                                  <span className="font-mono text-indigo-700">{a}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => { setPendingUpload(null); setPreviewData(null); }}
                    disabled={uploading || previewLoading}
                    data-testid="pa-upload-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    disabled={uploading || previewLoading}
                    onClick={() => doUpload("replace")} data-testid="pa-upload-replace">
              Replace Existing
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={uploading || previewLoading}
                    onClick={() => doUpload("upgrade")} data-testid="pa-upload-upgrade">
              {uploading && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
              Upgrade Only (Safe)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete alias mapping?</DialogTitle></DialogHeader>
          {confirmDelete && (
            <div className="text-sm text-slate-600">
              Delete <b>{confirmDelete.second_name}</b> and its {confirmDelete.aliases?.length || 0} alias(es)? This cannot be undone.
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={doDelete} data-testid="pa-confirm-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 244 — Duplicates finder + merge */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-4xl" data-testid="pa-dup-dialog">
          <DialogHeader>
            <DialogTitle>
              Find Duplicates ({dupGroups.length} group{dupGroups.length === 1 ? "" : "s"})
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2">
            Products whose names collapse to the same spelling after ignoring case, spaces, punctuation and
            dosage units ("ALMOX 500" = "ALMOX 500 MG"; MCG/ML stay distinct).
            Pick which row to keep — merged rows transfer their aliases, price, packing, and generic to the keeper.
          </div>
          {dupLoading ? (
            <div className="py-10 text-center text-slate-500 text-sm">Scanning 7k+ aliases…</div>
          ) : dupGroups.length === 0 ? (
            <div className="py-10 text-center text-emerald-700 text-sm font-medium" data-testid="pa-dup-empty">
              No duplicates found. Everything is unique.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
              {dupGroups.map(g => (
                <div key={g.key} className="border rounded-md p-2 bg-slate-50"
                     data-testid={`pa-dup-group-${g.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono text-slate-600">
                      key: <b>{g.key}</b> · {g.count} rows
                    </div>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                            disabled={merging === g.key || !dupKeep[g.key]}
                            onClick={() => mergeGroup(g)}
                            data-testid={`pa-dup-merge-${g.key}`}>
                      {merging === g.key
                        ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        : <GitMerge className="w-4 h-4 mr-1" />}
                      Merge {g.count - 1} into keeper
                    </Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-[10px] text-slate-500 uppercase">
                      <tr>
                        <th className="w-10 text-center">Keep</th>
                        <th className="text-left px-2">Second Name</th>
                        <th className="text-left px-2">Generic</th>
                        <th className="text-left px-2">Company</th>
                        <th className="text-right px-2">Price</th>
                        <th className="text-center px-2">Packing</th>
                        <th className="text-center px-2">Aliases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.docs.map(d => (
                        <tr key={d.id} className={dupKeep[g.key] === d.id ? "bg-emerald-50" : ""}>
                          <td className="text-center">
                            <input type="radio" name={`keep-${g.key}`}
                                   checked={dupKeep[g.key] === d.id}
                                   onChange={() => setDupKeep(p => ({ ...p, [g.key]: d.id }))}
                                   data-testid={`pa-dup-keep-${d.id}`} />
                          </td>
                          <td className="px-2 py-1 font-medium">{d.second_name}</td>
                          <td className="px-2 py-1">{d.generic_name || "—"}</td>
                          <td className="px-2 py-1">{d.company || "—"}</td>
                          <td className="px-2 py-1 text-right font-mono">{d.price || "—"}</td>
                          <td className="px-2 py-1 text-center font-mono">{d.packing || "—"}</td>
                          <td className="px-2 py-1 text-center">{(d.aliases || []).length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)} data-testid="pa-dup-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
